import React, { useRef, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { Icon } from '@chakra-ui/icons'
import { Button } from '@chakra-ui/react'
import { FaChevronLeft } from 'react-icons/fa6'

import {
    AppointmentFieldsFragment,
    AppointmentStatus,
    AuthorityLevel,
    ChatClinicFieldsFragment,
    ClientPreviewFieldsFragment,
    MessageTemplateFieldsFragment,
    useQueryAppointmentsQuery,
} from '~graphql/generated/graphql'
import MessageTemplatesList from './list'
import MessageTemplateOption from './list/option'
import MessageTemplatePatients from './patients'
import AppointmentSelect from '../appointment-select'
import useSelectedPatient from './use-selected-patient'
import SharedModal from '~components/shared/modals/shared-modal'
import SharedStack from '~components/shared/wrappers/shared-stack'
import { useToastFeedback } from '~utils/hooks/use-toast-feedback'
import { useStateWithReset } from '~utils/hooks/use-state-with-reset'
import {
    getTemplateMapper,
    hasAppointmentSelectionTemplateVariables,
    hasClientPreviousDoctorTemplateVariable,
    hasPatientPreviousDoctorTemplateVariable,
    replaceTemplateVariables,
} from '~utils/replace-template-variables'
import { PatientIdAndName } from './types'
import { getCopyTemplateMissingInfoHelp } from './missing-info-help'
import CopyTemplateMissingInfoBanner from './missing-info-banner'
import { SetStateFn } from '~config/constants'
import {
    eqBoolFilter,
    eqStringFilter,
    inStringArrFilter,
    lteFloatFilter,
} from '~utils/query-helpers'
import { constructOrderBy } from '~utils/querying'

const APPOINTMENT_GOOGLE_CALENDAR_LINK_QUERY = gql`
    query appointmentGoogleCalendarLink($appointmentId: ID!) {
        appointmentGoogleCalendarLink(appointmentId: $appointmentId)
    }
`

type AppointmentGoogleCalendarLinkQuery = {
    appointmentGoogleCalendarLink: string
}

type AppointmentGoogleCalendarLinkQueryVariables = {
    appointmentId: string
}

interface Props {
    isOpen: boolean
    onClose: () => void
    clinic: ChatClinicFieldsFragment
    client: ClientPreviewFieldsFragment
    patient?: PatientIdAndName
    appointment?: AppointmentFieldsFragment
    patientFilter?: string[]
    setText: (text: string) => void
    onCopyTemplatePatient?: (patient: PatientIdAndName | undefined) => void
}

type ModalPage = 'templates' | 'appointment'

export default function CopyTemplateModal({
    isOpen,
    onClose,
    clinic,
    client,
    patient,
    appointment,
    patientFilter,
    setText,
    onCopyTemplatePatient,
}: Props) {
    const {
        patients,
        selectedPatient,
        patientId,
        setPatientId,
        resetPatientId,
        showMore,
        setShowMore,
        needsShowMore,
    } = useSelectedPatient(
        client,
        patientFilter ?? (patient ? [patient.id] : []),
    )
    const { onCompleted, onError } = useToastFeedback(
        'Copied.',
        'You must select a template to copy.',
    )

    const [selectedTemplate, setSelectedTemplate, resetTemplate] =
        useStateWithReset<MessageTemplateFieldsFragment | undefined>(undefined)
    const [selectedAppointment, setSelectedAppointment] = useState<
        AppointmentFieldsFragment | undefined
    >(appointment)
    const [page, setPage] = useState<ModalPage>('templates')
    const nowRef = useRef(Date.now())

    const selectedTemplateHasAppointmentFields =
        hasAppointmentSelectionTemplateVariables(selectedTemplate?.body)
    const shouldResolveClientPreviousDoctor =
        hasClientPreviousDoctorTemplateVariable(selectedTemplate?.body)
    const shouldResolvePatientPreviousDoctor =
        hasPatientPreviousDoctorTemplateVariable(selectedTemplate?.body)
    const selectedAppointmentMatchesPatient =
        selectedAppointment?.patient?.id === selectedPatient?.id
    const recentDoctorAppointmentFilters = [
        eqStringFilter('clinic', clinic.id),
        eqStringFilter('clinician.authority_level', AuthorityLevel.Doctor),
        eqBoolFilter('clinician.pms_is_active', true),
        lteFloatFilter('start_time', nowRef.current),
        inStringArrFilter('status', [
            AppointmentStatus.Booked,
            AppointmentStatus.Confirmed,
            AppointmentStatus.Completed,
        ]),
    ]
    const { data: clientPreviousDoctorData, loading: isLoadingClientDoctor } =
        useQueryAppointmentsQuery({
            variables: {
                where: [
                    ...recentDoctorAppointmentFilters,
                    eqStringFilter('client', client.id),
                ],
                page: 0,
                pageSize: 1,
                orderBy: constructOrderBy('start_time', false),
            },
            skip:
                !shouldResolveClientPreviousDoctor || !clinic.id || !client.id,
        })
    const { data: patientPreviousDoctorData, loading: isLoadingPatientDoctor } =
        useQueryAppointmentsQuery({
            variables: {
                where: [
                    ...recentDoctorAppointmentFilters,
                    eqStringFilter('patient', selectedPatient?.id || ''),
                ],
                page: 0,
                pageSize: 1,
                orderBy: constructOrderBy('start_time', false),
            },
            skip:
                !shouldResolvePatientPreviousDoctor ||
                !clinic.id ||
                !selectedPatient?.id,
        })
    const clientPreviousDoctor = getAppointmentClinicianName(
        clientPreviousDoctorData?.queryAppointments.at(0),
    )
    const patientPreviousDoctor = getAppointmentClinicianName(
        patientPreviousDoctorData?.queryAppointments.at(0),
    )
    const calendarAppointmentId = selectedAppointmentMatchesPatient
        ? selectedAppointment?.id
        : undefined
    const { data: calendarLinkData, loading: isLoadingCalendarLink } = useQuery<
        AppointmentGoogleCalendarLinkQuery,
        AppointmentGoogleCalendarLinkQueryVariables
    >(APPOINTMENT_GOOGLE_CALENDAR_LINK_QUERY, {
        variables: { appointmentId: calendarAppointmentId || '' },
        skip: !calendarAppointmentId,
    })
    const googleCalendarEvent =
        calendarAppointmentId && calendarLinkData?.appointmentGoogleCalendarLink
            ? calendarLinkData.appointmentGoogleCalendarLink
            : ''
    const requiresPatientSelect =
        selectedTemplate?.body.includes('patient_name') ||
        selectedTemplateHasAppointmentFields ||
        shouldResolvePatientPreviousDoctor
    // Page 1 only asks for patient + template; the appointment step lives on
    // page 2, so appointment guidance waits until then.
    const templatePageHelp = getCopyTemplateMissingInfoHelp({
        missingPatient: !patientId,
        missingAppointment: false,
    })
    const appointmentPageHelp = getCopyTemplateMissingInfoHelp({
        missingPatient: false,
        missingAppointment:
            selectedTemplateHasAppointmentFields &&
            !selectedAppointmentMatchesPatient,
    })

    const handlePatientIdChange: SetStateFn<string | undefined> = value => {
        const nextPatientId =
            typeof value === 'function' ? value(patientId) : value
        setPatientId(nextPatientId)
        setSelectedAppointment(
            appointment?.patient?.id === nextPatientId
                ? appointment
                : undefined,
        )
    }

    function handleSelectedTemplateChange(
        template: MessageTemplateFieldsFragment | undefined,
    ) {
        setSelectedTemplate(template)

        if (!hasAppointmentSelectionTemplateVariables(template?.body)) {
            setSelectedAppointment(undefined)
            return
        }

        const appointmentMatchesPatient = appointment?.patient?.id === patientId
        const selectedMatchesPatient =
            selectedAppointment?.patient?.id === patientId

        if (!selectedMatchesPatient) {
            setSelectedAppointment(
                appointmentMatchesPatient ? appointment : undefined,
            )
        }
    }

    function handleReset() {
        resetTemplate()
        resetPatientId()
        setSelectedAppointment(appointment)
        setShowMore(false)
        setPage('templates')
    }

    const shouldAdvanceToAppointment =
        !!selectedTemplate && selectedTemplateHasAppointmentFields

    function handleConfirm() {
        if (page === 'templates' && shouldAdvanceToAppointment) {
            setPage('appointment')
            return
        }

        handleSave()
    }

    function handleSave() {
        if (!selectedTemplate) {
            onError()
            return
        }

        const { value: modifiedBody } = replaceTemplateVariables(
            selectedTemplate.body,
            getTemplateMapper({
                client: client,
                clinic,
                patient: selectedPatient,
                appointment: selectedAppointment,
                clientPreviousDoctor,
                patientPreviousDoctor,
                googleCalendarEvent,
            }),
        )

        setText(modifiedBody)
        onCopyTemplatePatient?.(selectedPatient)
        onCompleted()
    }

    return (
        <SharedModal
            size={{ base: 'full', md: '2xl', xl: '4xl' }}
            isOpen={isOpen}
            onClose={() => {
                handleReset()
                onClose()
            }}
            onCancel={handleReset}
            onConfirm={handleConfirm}
            skipCloseOnConfirm={
                page === 'templates' && shouldAdvanceToAppointment
            }
            confirmText={
                page === 'appointment'
                    ? 'Copy Message'
                    : shouldAdvanceToAppointment
                      ? 'Continue'
                      : 'Copy Message'
            }
            title={
                page === 'appointment'
                    ? 'Select an appointment'
                    : 'Copy message from a template'
            }
            isConfirmDisabled={
                !selectedTemplate || (requiresPatientSelect && !selectedPatient)
            }
            isLoading={
                isLoadingClientDoctor ||
                isLoadingPatientDoctor ||
                isLoadingCalendarLink
            }
            contentOverrides={{
                mx: { base: 0, md: 4 },
                my: { base: 0, md: 4 },
                maxH: { base: '100dvh', md: '90vh' },
            }}
            bodyOverrides={{
                overflowY: 'auto',
                maxH: { base: 'calc(100dvh - 148px)', md: '70vh' },
                px: { base: 4, md: 6 },
            }}
        >
            {page === 'templates' ? (
                <SharedStack spacing={{ base: 4, md: 6 }}>
                    {templatePageHelp && (
                        <CopyTemplateMissingInfoBanner
                            help={templatePageHelp}
                        />
                    )}
                    <MessageTemplatePatients
                        patients={patients}
                        patientId={patientId}
                        setPatientId={handlePatientIdChange}
                        needsShowMore={needsShowMore}
                        showMore={showMore}
                        setShowMore={setShowMore}
                    />
                    <MessageTemplatesList
                        clinic={clinic}
                        client={client}
                        selectedPatient={selectedPatient}
                        appointment={
                            selectedAppointmentMatchesPatient
                                ? selectedAppointment
                                : undefined
                        }
                        clientPreviousDoctor={clientPreviousDoctor}
                        patientPreviousDoctor={patientPreviousDoctor}
                        googleCalendarEvent={googleCalendarEvent}
                        selectedTemplate={selectedTemplate}
                        setSelectedTemplate={handleSelectedTemplateChange}
                    />
                </SharedStack>
            ) : (
                <SharedStack spacing={{ base: 4, md: 6 }}>
                    <Button
                        variant="ghost"
                        size="sm"
                        rounded="lg"
                        alignSelf="flex-start"
                        leftIcon={<Icon as={FaChevronLeft} />}
                        onClick={() => setPage('templates')}
                    >
                        Back to templates
                    </Button>
                    {appointmentPageHelp && (
                        <CopyTemplateMissingInfoBanner
                            help={appointmentPageHelp}
                        />
                    )}
                    {selectedTemplate && (
                        <MessageTemplateOption
                            clinic={clinic}
                            client={client}
                            template={selectedTemplate}
                            patient={selectedPatient}
                            appointment={
                                selectedAppointmentMatchesPatient
                                    ? selectedAppointment
                                    : undefined
                            }
                            clientPreviousDoctor={clientPreviousDoctor}
                            patientPreviousDoctor={patientPreviousDoctor}
                            googleCalendarEvent={googleCalendarEvent}
                            isSelected
                            query=""
                            readOnly
                        />
                    )}
                    {selectedPatient && (
                        <AppointmentSelect
                            clinicId={clinic.id}
                            patientId={selectedPatient.id}
                            patientName={selectedPatient.name}
                            selectedAppointment={
                                selectedAppointmentMatchesPatient
                                    ? selectedAppointment
                                    : undefined
                            }
                            setSelectedAppointment={setSelectedAppointment}
                        />
                    )}
                </SharedStack>
            )}
        </SharedModal>
    )
}

function getAppointmentClinicianName(
    appointment: AppointmentFieldsFragment | undefined,
): string {
    return (
        appointment?.clinician?.display_name ||
        appointment?.clinician?.name ||
        ''
    )
}
